import { cva } from 'class-variance-authority';
import { ErrorBoundary, Error } from './error';
import { cn } from '@/helpers/shadcn';

const pageVariants = cva(cn('flex-1 min-h-[calc(100vh-var(--app-header-height))] flex flex-col w-full mt-[var(--app-header-height)]'), {
  variants: {
    variant: {
      default: 'max-w-7xl mx-auto',
      wide: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

const Page = ({ children, variant = 'default', className, ...props }) => {
  return (
    <main data-slot="page" className={cn(pageVariants({ variant, className }))} {...props}>
      {children}
    </main>
  );
};

const PageHeadingVariants = cva(
  cn(
    '@container/page-heading grid auto-rows-min grid-rows-[auto_auto] items-start has-data-[slot=page-heading-action]:grid-cols-[1fr_auto] [&>[data-slot=breadcrumb]]:mb-2 px-3 py-6'
  ),
  {
    variants: {
      variant: {
        default: '',
        tab: '[&>[data-slot=page-title]]:text-base [&>[data-slot=page-description]]:text-sm '
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);
const PageHeading = ({ children, className, variant = 'default', ...props }) => {
  return (
    <div data-slot="page-heading" className={cn(PageHeadingVariants({ variant, className }))} {...props}>
      {children}
    </div>
  );
};

const PageTitle = ({ children, ...props }) => {
  return (
    <div data-slot="page-title" className="text-lg font-semibold flex items-center gap-2 [&>svg]:size-4 [&>svg]:text-muted-foreground" {...props}>
      {children}
    </div>
  );
};

const PageDescription = ({ children, ...props }) => {
  return (
    <div data-slot="page-description" className="text-muted-foreground flex items-center gap-1 [&>svg]:size-4 text-sm" {...props}>
      {children}
    </div>
  );
};

const PageAction = ({ children, ...props }) => {
  return (
    <div data-slot="page-action" className="flex items-center gap-2 col-start-2 row-span-2 row-start-1 self-start justify-self-end" {...props}>
      {children}
    </div>
  );
};

const PageContent = ({ children, className, ...props }) => {
  return (
    <div data-slot="page-content" className={cn('flex-1 relative p-3 pt-0 flex flex-col', className)} {...props}>
      <ErrorBoundary>{children}</ErrorBoundary>
    </div>
  );
};

const pageLoadingVariants = cva(cn('relative overflow-hidden w-full h-full min-h-(--page-height)'), {
  variants: {
    variant: {
      default: 'pt-(--app-header-height)',
      app: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

const PageLoading = ({ className, variant = 'default' }) => {
  return (
    <div className={cn(pageLoadingVariants({ variant, className }))}>
      <div className="absolute w-full h-1 bg-foreground/10">
        <div className="bg-secondary dark:bg-secondary/70 h-1 origin-left animate-progress z-1" role="status" aria-label="Loading" />
      </div>
    </div>
  );
};

// `dataTestId` is pulled out of the spread: the wrapper is a plain `div`, and React
// warns (a `console.error`, which the Playwright guard fails on) about a camelCase
// prop reaching a DOM element. `Error` puts it on the alert itself.
const PageError = ({ className, dataTestId, ...props }) => {
  return (
    <div className={cn('p-12 my-12', className)} {...props}>
      <Error dataTestId={dataTestId} {...props} />
    </div>
  );
};

export { Page, PageHeading, PageContent, PageTitle, PageDescription, PageAction, PageLoading, PageError };
